// Ported from dev-scrips-pm-react src/documents/services/use-organization-forms.ts (PROD-1119 documents module).
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * GET /api/Patients/OrganizationForm
 *
 * Org-level form templates the user picks from when creating a
 * "Form"-type document. Andrew confirmed 2026-05-15: the flow is
 *   1. User opens NewFilePopup
 *   2. Picks "Form" as the document type
 *   3. Picks a template from this dropdown
 *   4. Downloads the blank template (uses `template.url`)
 *   5. Fills it offline (PDF / DOC)
 *   6. Re-uploads the filled file via the same NewFilePopup
 *
 * Endpoint takes no body and no query params — it's tenant-scoped via
 * the auth headers (OrganizationId).
 *
 * Response shape (CURL-VERIFIED LIVE 2026-06-10, dev appointment Docs):
 * ```json
 * [
 *   {
 *     "id": "75adcce4-…",
 *     "organizationId": "1d8ce992-…",
 *     "formName": "CRHI-FCC-OP-Form-24-Commitment Letter",
 *     "formPath": "https://…blob.core.windows.net/form/….pdf?sv=…&sig=…"
 *   }
 * ]
 * ```
 * The download target is **`formPath`** (a SAS-signed Azure blob URL),
 * NOT `url`/`fileName`. The display name is `formName`. There is no
 * separate filename field — derive the download filename from the
 * `formPath` blob path (`resolveFormTemplateFilename` in
 * `new-file-popup.tsx`). `[key: string]: unknown` kept so an additive
 * backend field doesn't break the typing.
 */

export interface OrganizationForm {
  id?: string;
  organizationId?: string;
  /** Template display name (bound to the document `name`). */
  formName?: string;
  /**
   * SAS-signed Azure blob URL for the blank template (download target).
   * THE canonical URL field — verified 2026-06-10.
   */
  formPath?: string;
  /** Legacy/fallback display name (not present on the dev wire). */
  name?: string;
  /** Allow extra fields without breaking the typing. */
  [key: string]: unknown;
}

export function useOrganizationForms(opts: { enabled?: boolean } = {}) {
  return useQuery<OrganizationForm[]>({
    queryKey: ['organization-forms'],
    enabled: opts.enabled ?? true,
    queryFn: ({ signal }) =>
      get<OrganizationForm[]>('/api/Patients/OrganizationForm', { signal }),
    // Templates change rarely — cache for an hour. Filter widgets and
    // the popup can serve from this cache without re-fetching every
    // time the popup opens.
    staleTime: 60 * 60 * 1000,
  }) as UseQueryResult<OrganizationForm[]>;
}
